import { AbsgArrayHelper } from '@absg/angular-common';
import { CalculationHelper, CalculatorChangeInitiatorTypes, ConversionHelper, DispenseTypes, InventoryDisplay, InventoryItem } from '@absg/nim-client-shared';
import { DispenseSelectedGroup } from 'dispense.models/dispense-selected-group.model';
import { MedOrderDispenseDetail } from 'shared.root/models/med-order-detail';

export class MedOrderDispenseHelper {
  static getUomMatch(inventoryItem: InventoryItem, medOrderDetail: MedOrderDispenseDetail): CalculatorChangeInitiatorTypes {
    if (!medOrderDetail || !medOrderDetail.detail || !inventoryItem) { return CalculatorChangeInitiatorTypes.None; }
    const doseType = medOrderDetail.getMedOrderDoseUnit();
    if (inventoryItem.dispenseUnit?.toLowerCase() === doseType?.toLowerCase()) { return CalculatorChangeInitiatorTypes.Dispense; }
    if (inventoryItem.inventoryUnit?.toLowerCase() === doseType?.toLowerCase()) { return CalculatorChangeInitiatorTypes.Inventory; }
    if (inventoryItem.volumeUnit?.toLowerCase() === doseType?.toLowerCase()) { return CalculatorChangeInitiatorTypes.Volume; }
    return CalculatorChangeInitiatorTypes.None;
  }

  static isItemQtyInsufficientToFulfillOrder(
    invDisplay: InventoryDisplay,
    medOrderDetail: MedOrderDispenseDetail,
    configuredMedOrders: DispenseSelectedGroup[] = [],
  ): boolean {
    if (!medOrderDetail || !medOrderDetail.detail || !invDisplay || !invDisplay.defaultInventoryItem) { return false; }
    if (MedOrderDispenseHelper.isInventoryItemAlreadyConfiguredForMedOrder(invDisplay.defaultInventoryItem, medOrderDetail, configuredMedOrders)) {
      return false;
    }
    const inventoryItem = invDisplay.defaultInventoryItem;
    const uomMatch = this.getUomMatch(inventoryItem, medOrderDetail);
    const doseAmount = medOrderDetail.getMedOrderDoseAmount();
    const previousAmount = CalculationHelper.add(medOrderDetail.dispensedAmount, medOrderDetail.configuredAmount);
    const dispenseFactor = inventoryItem.dispenseUOMConversionFactor;
    let convertedAmount = 0;
    const qoh = invDisplay.isBin ? invDisplay.totalQuantityInBin : inventoryItem.getQuantityOnHand();
    if (qoh === 0) { return true; }
    if (doseAmount <= previousAmount) { return false; }

    switch (uomMatch) {
      case CalculatorChangeInitiatorTypes.Volume:
        if (!inventoryItem.volumeUOMConversionFactor) { break; }
        convertedAmount =
          CalculationHelper.convertToQuantity(doseAmount, inventoryItem.volumeUOMConversionFactor, dispenseFactor);
        return MedOrderDispenseHelper.isAmountAvailableToUseInsufficient(
          invDisplay, convertedAmount, previousAmount, configuredMedOrders, medOrderDetail);
      case CalculatorChangeInitiatorTypes.Inventory:
        if (!inventoryItem.inventoryUOMConversionFactor) { break; }
        convertedAmount =
          CalculationHelper.convertToQuantity(doseAmount, inventoryItem.inventoryUOMConversionFactor, dispenseFactor);
        return MedOrderDispenseHelper.isAmountAvailableToUseInsufficient(
          invDisplay, convertedAmount, previousAmount, configuredMedOrders, medOrderDetail);
      case CalculatorChangeInitiatorTypes.Dispense:
        return MedOrderDispenseHelper.isAmountAvailableToUseInsufficient(
          invDisplay, doseAmount, previousAmount, configuredMedOrders, medOrderDetail);
      case CalculatorChangeInitiatorTypes.None: {
        const conversionAmount =
          ConversionHelper.convertQuantityByUnitName(doseAmount, medOrderDetail.getMedOrderDoseUnit(), inventoryItem.dispenseUnit);
        if (conversionAmount === 0) { return false; }
        return MedOrderDispenseHelper.isAmountAvailableToUseInsufficient(
          invDisplay, doseAmount, previousAmount, configuredMedOrders, medOrderDetail);
      }
      default:
        return false;
    }
    return false;
  }

  static getAmountToPopulate(
    inventoryItem: InventoryItem,
    amountDose: number,
    previousAmount = 0,
    shouldConvert = false,
    doseType = '',
    dispenseType = '',
    configuredMedOrders: DispenseSelectedGroup[] = [],
    medOrderDetail: MedOrderDispenseDetail = null,
    invDisplay: InventoryDisplay = null,
  ): number {
    if (!inventoryItem || amountDose < 0) { return 0; }
    if (shouldConvert) {
      if (!dispenseType || !doseType) { return 0; }
      amountDose = ConversionHelper.convertQuantityByUnitName(amountDose, doseType, dispenseType);
    }
    const availableAmount = inventoryItem.isBin ?
      this.getAvailableQuantityForItemOrBin(invDisplay, configuredMedOrders, medOrderDetail) :
      this.getAvailableQuantity(inventoryItem, configuredMedOrders, medOrderDetail);
    const neededAmount = CalculationHelper.subtract(amountDose, previousAmount);
    // chk if the dispense type is whole dispense and amount needed can cause fraction amount
    if (inventoryItem.dispenseTypeId === DispenseTypes.WholeDispense) {
      const containersize = inventoryItem.dispenseUOMConversionFactor / inventoryItem.inventoryUOMConversionFactor;
      const isFractionAmountCreated = neededAmount % containersize > 0;
      if (isFractionAmountCreated) {
        return 0;
      }
    }
    return availableAmount >= neededAmount && neededAmount > 0 ? neededAmount : 0;
  }

  static getAvailableQuantity(
    inventoryItem: InventoryItem,
    configuredMedOrders: DispenseSelectedGroup[] = [],
    medOrderDetail: MedOrderDispenseDetail = null,
  ): number {
    if (!inventoryItem) { return 0; }
    const inventoryAvailableQuantity = CalculationHelper.add(inventoryItem.quantityUnderHood, inventoryItem.quantityInCabinet);
    return CalculationHelper.subtract(inventoryAvailableQuantity,
      this.getAlreadyAssignedMedOrdersQuantity(inventoryItem.supplierProductInclusionId, configuredMedOrders, medOrderDetail));
  }

  static getAvailableQuantityForItemOrBin(
    invDisplay: InventoryDisplay,
    configuredMedOrders: DispenseSelectedGroup[] = [],
    medOrderDetail: MedOrderDispenseDetail = null,
  ): number {
    if (!invDisplay || !invDisplay.defaultInventoryItem) { return 0; }
    const inventoryItem = invDisplay.defaultInventoryItem;
    const inventoryAvailableQuantity = invDisplay.isBin ? invDisplay.totalQuantityInBin :
      inventoryItem.getQuantityOnHand();
    return CalculationHelper.subtract(inventoryAvailableQuantity,
      this.getAlreadyAssignedMedOrdersQuantity(inventoryItem.supplierProductInclusionId, configuredMedOrders, medOrderDetail));
  }

  static getAlreadyAssignedMedOrdersQuantity(
    supplierProductInclusionId: number,
    configuredMedOrders: DispenseSelectedGroup[],
    excludedMedOrderDetail: MedOrderDispenseDetail,
  ): number {
    let alreadyAssignedMedOrdersQuantity = 0;
    AbsgArrayHelper.forEach(configuredMedOrders, configuredMedOrder => {
      if (!excludedMedOrderDetail || excludedMedOrderDetail.getMedOrderId() !== configuredMedOrder.getMedOrderId()) {
        const assignedInventoryItem = AbsgArrayHelper.find(configuredMedOrder.dispenseItems,
          dispenseItem => dispenseItem.inventoryItem.supplierProductInclusionId === supplierProductInclusionId);
        if (assignedInventoryItem) {
          alreadyAssignedMedOrdersQuantity += assignedInventoryItem.inventoryItem.getSelectedQuantity();
        }
      }
    });
    return alreadyAssignedMedOrdersQuantity;
  }

  private static isAmountAvailableToUseInsufficient(
    invDisplay: InventoryDisplay,
    amountDose: number,
    previousAmount: number,
    configuredMedOrders: DispenseSelectedGroup[],
    medOrderDetail: MedOrderDispenseDetail,
  ): boolean {
    if (invDisplay && invDisplay.defaultInventoryItem && amountDose > 0 &&
      medOrderDetail.getMedOrderDoseUnit() && invDisplay.defaultInventoryItem.dispenseUnit) {
      let amountInDispenseUnit = amountDose;
      if (medOrderDetail.getMedOrderDoseUnit() !== invDisplay.defaultInventoryItem.dispenseUnit) {
        // convert the numbers
        amountInDispenseUnit = ConversionHelper.convertQuantityByUnitName(amountDose,
          medOrderDetail.getMedOrderDoseUnit(), invDisplay.defaultInventoryItem.dispenseUnit);
      }
      // compare
      const availableAmount = this.getAvailableQuantityForItemOrBin(invDisplay, configuredMedOrders, medOrderDetail);
      const neededAmount = CalculationHelper.subtract(amountInDispenseUnit, previousAmount);
      return neededAmount > availableAmount;
    }
    return false;
  }

  private static isInventoryItemAlreadyConfiguredForMedOrder(
    inventoryItem: InventoryItem,
    medOrderDetail: MedOrderDispenseDetail,
    configuredMedOrders: DispenseSelectedGroup[],
  ): boolean {
    const previouslyConfiguredMedOrder = AbsgArrayHelper.find(configuredMedOrders,
      configuredMedOrder => configuredMedOrder.medOrderDetail.getMedOrderId() === medOrderDetail.getMedOrderId());

    if (previouslyConfiguredMedOrder) {
      if (AbsgArrayHelper.find(previouslyConfiguredMedOrder.dispenseItems,
        dispenseItem => dispenseItem.inventoryItem.supplierProductInclusionId === inventoryItem.supplierProductInclusionId)) {
        return true;
      }
    }

    return false;
  }

}
